import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Send, Users, Copy, Share2, X } from "lucide-react";
import { useNativeShare } from "@/hooks/useNativeShare";

/**
 * ReflectedShareModal — shown right after an installment is marked as
 * "reflejado". The confirmation text was already pushed to the client's
 * chat; this panel lets the admin forward the same text by WhatsApp.
 *
 * data: { message, client_name, client_phone, amount, currency, service_name }
 */
export const ReflectedShareModal = ({ data, onClose }) => {
  const { canShare, share } = useNativeShare();
  if (!data) return null;
  const digits = String(data.client_phone || "").replace(/\D/g, "");
  const encoded = encodeURIComponent(data.message || "");
  const waDirect = digits ? `https://wa.me/${digits}?text=${encoded}` : null;
  const waPick = `https://wa.me/?text=${encoded}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.message); toast.success("Mensaje copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };
  const nativeShare = async () => {
    const r = await share({ text: data.message, title: `Pago reflejado · ${data.client_name}` });
    if (r.ok) toast.success("Compartido");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[170] bg-black/85 backdrop-blur-md flex items-center justify-center px-3"
      data-testid="reflected-share-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border-2 border-emerald-500/50 rounded-2xl p-5 shadow-[0_0_60px_rgba(16,185,129,0.3)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button data-testid="reflected-share-close" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          <h3 className="font-serif text-lg text-white">Pago marcado como reflejado</h3>
        </div>
        <p className="text-xs text-neutral-400 mb-3">
          Ya se envió la confirmación al chat de <b className="text-white">{data.client_name}</b>. Compártela también por WhatsApp:
        </p>
        <div data-testid="reflected-share-message" className="mb-3 rounded-lg bg-black/40 border border-emerald-500/20 p-3 text-xs text-neutral-200 whitespace-pre-wrap leading-relaxed max-h-44 overflow-y-auto">
          {data.message}
        </div>
        <div className="space-y-2">
          {canShare && (
            <button data-testid="reflected-share-native" onClick={nativeShare} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black font-bold text-xs uppercase tracking-widest">
              <Share2 className="w-3.5 h-3.5" /> Compartir con app…
            </button>
          )}
          {waDirect ? (
            <a data-testid="reflected-share-wa-client" href={waDirect} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs uppercase tracking-widest">
              <Send className="w-3.5 h-3.5" /> WhatsApp a {data.client_name} · {data.client_phone}
            </a>
          ) : (
            <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-center">Este cliente no tiene teléfono registrado — usa el selector de contacto.</div>
          )}
          <a data-testid="reflected-share-wa-contact" href={waPick} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-200 hover:bg-white/5 text-xs">
            <Users className="w-3.5 h-3.5" /> Elegir contacto de WhatsApp
          </a>
          <button data-testid="reflected-share-copy" onClick={copy} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-400 hover:text-white hover:bg-white/5 text-xs">
            <Copy className="w-3.5 h-3.5" /> Copiar mensaje
          </button>
        </div>
        <button data-testid="reflected-share-done" onClick={onClose} className="mt-4 w-full text-[10px] text-neutral-500 hover:text-white uppercase tracking-widest">Listo</button>
      </div>
    </motion.div>
  );
};

export default ReflectedShareModal;
